import React from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {
  useGetSavedForLaterItemIdsQuery,
  useGetSavedForLaterItemsDataQuery,
} from "../../api/savedForLaterApi";
import PageSavedForLaterItem from "./PageSavedForLaterItem";

const PageSavedForLaterItemSkeleton: React.FC = () => (
  <div className="page-saved-for-later-item-wrapper">
    <div className="page-saved-for-later-item">
      {/* Image Column */}
      <div className="page-saved-for-later-item__image-col">
        <Skeleton
          style={{
            aspectRatio: "1/1",
            display: "block",
            width: "100%",
            lineHeight: 1,
          }}
          borderRadius={4}
        />
      </div>

      {/* Action Column */}
      <div className="page-saved-for-later-item__action-col">
        {/* Price */}
        <div className="page-saved-for-later-item__price-wrapper">
          <Skeleton width={80} height={24} />
        </div>

        {/* Buttons */}
        <div className="page-saved-for-later-item__buttons">
          <Skeleton width={100} height={36} />
          <Skeleton width={80} height={36} style={{ marginLeft: "0.5rem" }} />
        </div>
      </div>
    </div>
  </div>
);

const PageSavedForLaterListSkeleton: React.FC = () => (
  <div className="page-saved-for-later-list">
    <h2 className="page-saved-for-later-list__title">
      <Skeleton width={250} />
    </h2>
    <div className="page-saved-for-later-list__grid">
      {Array.from({ length: 2 }).map((_, i) => (
        <PageSavedForLaterItemSkeleton key={i} />
      ))}
    </div>
  </div>
);

const PageSavedForLaterList: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();

  // Step 1: Retrieve IDs from LocalStorage
  const { data: itemIds, isLoading: isIdsLoading } =
    useGetSavedForLaterItemIdsQuery();

  // Step 2: Fetch details from API only if we have IDs
  const shouldFetchData = !!itemIds && itemIds.length > 0;
  const {
    data: products,
    isLoading: areProductsLoading,
    isError: areProductsError,
  } = useGetSavedForLaterItemsDataQuery(itemIds || [], {
    skip: !shouldFetchData,
  });

  const isComponentLoading =
    isIdsLoading ||
    (shouldFetchData && areProductsLoading) ||
    !areTranslationsReady;

  if (isComponentLoading) return <PageSavedForLaterListSkeleton />;

  // If empty or error, hide the section entirely
  if (!itemIds || itemIds.length === 0) return null;
  if (areProductsError || !products || products.length === 0) return null;

  return (
    <div className="page-saved-for-later-list">
      <h2 className="page-saved-for-later-list__title">
        {t("savedForLater.title", "Saved for Later")} ({products.length})
      </h2>
      <div className="page-saved-for-later-list__grid">
        {products.map((item) => (
          <PageSavedForLaterItem key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
};

export default PageSavedForLaterList;
